import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import styled from 'styled-components';
import qs from 'qs';

import { getBrandMetrics } from 'actions/index';
import history, { replace } from 'modules/history';
import { Colors } from 'modules/theme';

import AlertBreakdownTooltip from './AlertBreakdownTooltip';
import StatCard from './StatCard';
import TimeRangePicker from './TimeRangePicker';
import XOverY from './XOverY';

const Wrapper = styled.section`
  padding: 18px 24px 8px;
  background: ${Colors.lightGray};
  border-bottom: 1px solid ${Colors.gray2};
`;

const TopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 14px;
  flex-wrap: wrap;
`;

const LeftSlot = styled.div`
  flex: 1 1 260px;
  min-width: 0;
  max-width: 420px;
  display: flex;
  align-items: center;
  @media (max-width: 768px) {
    flex-basis: 100%;
    max-width: 100%;
  }
`;

const RightSlot = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
`;

const CardsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const INSTALLED_TOOLTIP =
  "Devices that are live in the field / physical locations they're installed at. Each physical location may have multiple devices (Softener, RO, Water Filter).";
const PENDING_TOOLTIP =
  'Devices created in Aquarian but not yet installed / physical locations waiting on at least one install.';
const ALERT_TOOLTIP = 'Physical locations with one or more pending alerts.';

class BrandListMetrics extends React.Component {
  static propTypes = {
    actions: PropTypes.node,
    children: PropTypes.node,
    dispatch: PropTypes.func.isRequired,
    metrics: PropTypes.object.isRequired,
  };

  componentDidMount() {
    const range = this.readRangeFromUrl();
    this.fetch(range);
  }

  readRangeFromUrl = () => {
    const q = qs.parse((history.location.search || '').replace(/^\?/, ''));
    return {
      from: q.from || null,
      to: q.to || null,
    };
  };

  writeRangeToUrl = range => {
    const existing = qs.parse((history.location.search || '').replace(/^\?/, ''));
    const merged = { ...existing };
    if (range.from) merged.from = range.from;
    else delete merged.from;
    if (range.to) merged.to = range.to;
    else delete merged.to;

    const search = qs.stringify(merged);
    replace({ pathname: history.location.pathname, search: search ? `?${search}` : '' });
  };

  fetch = range => {
    const params = {};
    if (range.from) params.from = range.from;
    if (range.to) params.to = range.to;
    this.props.dispatch(getBrandMetrics(params));
  };

  handleRangeChange = range => {
    this.writeRangeToUrl(range);
    this.fetch(range);
  };

  render() {
    const { metrics, children, actions } = this.props;
    const { loading, loaded, totals, range } = metrics;
    const showSkeleton = loading && !loaded;

    return (
      <Wrapper>
        <TopRow>
          <LeftSlot>{children}</LeftSlot>
          <RightSlot>
            <TimeRangePicker range={range} onChange={this.handleRangeChange} />
            {actions}
          </RightSlot>
        </TopRow>

        <CardsRow>
          <StatCard label="Installed" tooltip={INSTALLED_TOOLTIP} loading={showSkeleton}>
            <XOverY
              devices={totals.devices_installed}
              sites={totals.sites_with_installed}
              size={28}
            />
          </StatCard>

          <StatCard
            label="Pending"
            tooltip={PENDING_TOOLTIP}
            loading={showSkeleton}
            footer="Current state, ignores time range"
          >
            <XOverY devices={totals.devices_pending} sites={totals.sites_with_pending} size={28} />
          </StatCard>

          <StatCard
            label="In Alert"
            tooltip={ALERT_TOOLTIP}
            loading={showSkeleton}
            footer="Current state, ignores time range"
            accent={totals.sites_in_alert > 0 ? 'red' : 'default'}
          >
            <AlertBreakdownTooltip
              total={totals.sites_in_alert || 0}
              breakdown={totals.alert_breakdown || []}
              size={28}
              iconSize={16}
            />
          </StatCard>
        </CardsRow>
      </Wrapper>
    );
  }
}

const mapStateToProps = state => ({
  metrics: state.brands.metrics,
});

export default connect(mapStateToProps)(BrandListMetrics);
